// The fields `filterArts` recognizes as toggleable -- see filter.js. Keeping
// this list here (rather than importing it) avoids coupling the attribute
// parser to filter.js's exports for what is really just a fixed vocabulary.
const TOGGLE_TYPES = ['tag', 'commissioner', 'artist', 'category', 'city', 'year']

/**
 * Parses the `toggles` attribute of the embeddable map into the quick filters
 * the control bar renders.
 *
 * Each embed decides which toggles it shows, which is the whole point: a
 * partner site running an ArtSites page wants that one button, not every tag
 * in the collection.
 *
 * The syntax is a comma-separated list of entries, each optionally typed and
 * optionally labelled:
 *
 *   toggles="ArtSites=ArtSites New Haven,commissioner:Site Projects"
 *
 * yields a button reading "ArtSites" that filters on the tag
 * "ArtSites New Haven", and a second reading "Site Projects" that filters on
 * `commissioner` "Site Projects" instead of `tag`. Labels exist because the
 * curated tags are long -- the client describes the feature as "an ArtSites
 * view", not as a tag name.
 *
 * An entry is one of:
 *
 *   type:Label=Value   -- explicit field, explicit label
 *   Label=Value         -- tag (the default field), explicit label
 *   Value                -- tag, label same as value
 *
 * The `type:` prefix is optional and, when present, must name one of the
 * fields `filterArts` recognizes -- `tag`, `commissioner`, `artist`,
 * `category`, `city`, or `year` -- matched case-insensitively and stored
 * lowercased. It is only recognized when the colon appears before the first
 * `=`, so a value containing a colon (e.g. a time) never gets mistaken for
 * one. A word before a colon that ISN'T one of those fields is treated as
 * plain label/value text rather than a typo'd type: a tag or label happening
 * to contain a literal colon is far more likely than an authoring mistake, so
 * rejecting it outright would silently drop a toggle the embed intended.
 * Entries with no type prefix default to `tag`, which is why every existing
 * embed keeps behaving exactly as it does today.
 *
 * Everything is trimmed for the same reason `filterArts` trims its terms:
 * these are authored attributes where stray whitespace is an accident, and an
 * untrimmed value silently matches nothing.
 *
 * @param {string} attribute Raw attribute value. Blank yields no toggles.
 * @returns {Array<{label: string, type: string, value: string}>} In the
 *   order authored.
 */
export function parseToggles(attribute) {
  if (typeof attribute !== 'string' || !attribute.trim()) return []

  return attribute
    .split(',')
    .map(entry => {
      // Only recognize `type:` when the colon comes before the first `=` --
      // otherwise a value like "a:b=c" would get its "a" mistaken for a type.
      let type = 'tag'
      let rest = entry
      const colonIndex = entry.indexOf(':')
      const equalsIndex = entry.indexOf('=')
      if (colonIndex !== -1 && (equalsIndex === -1 || colonIndex < equalsIndex)) {
        const candidate = entry.slice(0, colonIndex).trim().toLowerCase()
        if (TOGGLE_TYPES.includes(candidate)) {
          type = candidate
          rest = entry.slice(colonIndex + 1)
        }
        // else: not a recognized type, so `rest` stays the full entry and the
        // colon is parsed as ordinary label/value text below.
      }

      // Only the FIRST `=` (after any type prefix is stripped) separates
      // label from value: a value containing one is far likelier than a
      // label doing so.
      const separator = rest.indexOf('=')
      const label = separator === -1 ? rest : rest.slice(0, separator)
      const value = separator === -1 ? rest : rest.slice(separator + 1)

      return { label: label.trim(), type, value: value.trim() }
    })
    .filter(toggle => toggle.value && toggle.label)
}

export default parseToggles
